import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { 
  ARSettingsState, 
  ARSettingsData, 
  SaveIntegrationRequest, 
  ApplyFontsRequest, 
  CreateTranslationsRequest,
  ARDisplayType,
  FontConfig 
} from './types';
import { endpoints, formatErrors, baseQuery } from 'utils/api/endpoints';
import { ApiResponse } from 'types/ApiResponse';

export const initialState: ARSettingsState = {
  arDisplayType: 'all-in-one-button',
  arButtonFont: {
    name: '',
    url: '',
    isValid: true,
  },
  arPopupFont: {
    name: '',
    url: '',
    isValid: true,
  },
  loading: {
    saveIntegration: false,
    applyFonts: false,
    createTranslations: false,
    fetchSettings: false,
  },
  error: null,
  success: null,
};

// Redux slice for local state management
export const slice = createSlice({
  name: 'arSettings',
  initialState,
  reducers: {
    setDisplayType(state, action: PayloadAction<ARDisplayType>) {
      state.arDisplayType = action.payload;
    },
    setButtonFont(state, action: PayloadAction<FontConfig>) {
      state.arButtonFont = action.payload;
    },
    setPopupFont(state, action: PayloadAction<FontConfig>) {
      state.arPopupFont = action.payload;
    },
    setLoading(state, action: PayloadAction<Partial<ARSettingsState['loading']>>) {
      state.loading = { ...state.loading, ...action.payload };
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.success = null;
    },
    setSuccess(state, action: PayloadAction<string | null>) {
      state.success = action.payload;
      state.error = null;
    },
    clearMessages(state) {
      state.error = null;
      state.success = null;
    },
    resetState: () => initialState,
  },
});

export const { actions: arSettingsActions } = slice;

// RTK Query API for server communication
export const api = createApi({
  reducerPath: 'arSettingsApi',
  baseQuery,
  tagTypes: ['ARSettings'],
  endpoints: build => ({
    // Get current AR settings
    getARSettings: build.query<ApiResponse<ARSettingsData>, void>({
      query: () => ({
        url: endpoints.getARSettings.url,
        method: endpoints.getARSettings.method,
      }),
      providesTags: ['ARSettings'],
      transformErrorResponse(baseQueryReturnValue) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),

    // Save AR integration settings
    saveARIntegration: build.mutation<ApiResponse<ARSettingsData>, SaveIntegrationRequest>({
      query: (data) => ({
        url: endpoints.saveARIntegration.url,
        method: endpoints.saveARIntegration.method,
        body: data,
      }),
      invalidatesTags: ['ARSettings'],
      transformErrorResponse(baseQueryReturnValue) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),

    // Update AR fonts
    updateARFonts: build.mutation<ApiResponse<ARSettingsData>, ApplyFontsRequest>({
      query: (data) => ({
        url: endpoints.updateARFonts.url,
        method: endpoints.updateARFonts.method,
        body: data,
      }),
      invalidatesTags: ['ARSettings'],
      transformErrorResponse(baseQueryReturnValue) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),

    // Create AR translations
    createARTranslations: build.mutation<ApiResponse<{ message: string }>, CreateTranslationsRequest>({
      query: (data) => ({
        url: endpoints.createARTranslations.url,
        method: endpoints.createARTranslations.method,
        body: data,
      }),
      transformErrorResponse(baseQueryReturnValue) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),
  }),
});

export const useARSettingsSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  
  return {
    // Redux actions
    actions: arSettingsActions,
    
    // RTK Query hooks
    useGetARSettingsQuery: api.useGetARSettingsQuery,
    useLazyGetARSettingsQuery: api.useLazyGetARSettingsQuery,
    useSaveARIntegrationMutation: api.useSaveARIntegrationMutation,
    useUpdateARFontsMutation: api.useUpdateARFontsMutation,
    useCreateARTranslationsMutation: api.useCreateARTranslationsMutation,
  };
};

/**
 * Example Usage:
 *
 * export function ARSettingsComponent() {
 *  const { actions, useGetARSettingsQuery, useSaveARIntegrationMutation } = useARSettingsSlice();
 *  const dispatch = useDispatch();
 *  const { data, isLoading } = useGetARSettingsQuery();
 *  const [saveIntegration] = useSaveARIntegrationMutation();
 *
 *  const handleSave = (displayType) => {
 *    dispatch(actions.setLoading({ saveIntegration: true }));
 *    saveIntegration({ displayType });
 *   };
 * }
 */ 